import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip, Table, TableColumn, Typography } from '@scale/scaleui';
import { TaskStatus } from '@scale/shared/defaults';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { ToolsLink } from 'react-scale/components/ExpertManagement/ExpertManagementPage/ToolsLink';
import { fetchDolphinTasksForExpert } from 'react-scale/components/ExpertManagement/ExpertManagementPage/api';
import {
  StatContextState,
  apiStatContextSelector,
  dateRangeSelector,
  projectIdSelector,
  useStatContextStore,
} from 'react-scale/components/ExpertManagement/stores/statContext.store';
import { shallow } from 'zustand/shallow';
import { LoadingTable } from './LoadingTable';

const getChipClassForReviewStatus = (reviewStatus: TaskStatus): string => {
  switch (reviewStatus) {
    case TaskStatus.Completed:
      return 'bg-success-400 text-neutral-50';
    case TaskStatus.Pending:
    case TaskStatus.Processing:
    case TaskStatus.Canceled:
    case TaskStatus.Finalizing:
    case TaskStatus.BenchmarkGroundTruth:
      return 'bg-danger-400 text-neutral-50';
    case TaskStatus.Error:
      return 'bg-warning-300';
    default:
      return '';
  }
};

interface DolphinAuditTasksTableProps {
  expertId: string;
}

const selectors = (state: StatContextState) => ({
  projectId: projectIdSelector(state),
  dateRange: dateRangeSelector(state),
  apiStatContext: apiStatContextSelector(state),
});

export const DolphinAuditTasksTable: React.FC<DolphinAuditTasksTableProps> = ({ expertId }) => {
  const { projectId, dateRange, apiStatContext } = useStatContextStore(selectors, shallow);
  const { data, isLoading } = useQuery([`tasksForExpert`, expertId, projectId, dateRange], () =>
    fetchDolphinTasksForExpert(apiStatContext, expertId),
  );

  const tasks = useMemo(() => data?.tasks, [data]);

  const DolphinAuditTasksTableColumns: TableColumn[] = useMemo(
    () => [
      {
        id: 'projectName',
        header: 'Project',
        cell: function ProjectCell(props: any) {
          const { projectName, projectId } = props.row.original;
          return (
            <ToolsLink to={`/lookup/${projectId}`} className="flex items-center">
              <Typography.Paragraph2>{projectName}</Typography.Paragraph2>
              <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
            </ToolsLink>
          );
        },
      },
      {
        id: 'id',
        header: 'Task Id',
        cell: function ProjectCell(props: any) {
          const { id } = props.row.original;
          return (
            <ToolsLink to={`/lookup/${id}`} className="flex items-center">
              <Typography.Paragraph2>{id}</Typography.Paragraph2>
              <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
            </ToolsLink>
          );
        },
      },
      {
        id: 'reviewLevel',
        header: 'Review Level',
      },
      {
        id: 'status',
        header: 'Status',
        cell: function ReviewStatusCell(props: any) {
          const { status } = props.row.original;
          return (
            <Chip className={getChipClassForReviewStatus(status as TaskStatus)} label={status} />
          );
        },
      },
      {
        id: 'promptQualityScore',
        header: 'Prompt Quality',
        headerTooltip: 'Score ranges from 0-3',
      },
      {
        id: 'responseAgreement',
        header: 'Response Agreement',
        cell: function ResponseAgreementCell(props: any) {
          const { responseAgreement } = props.row.original;
          return responseAgreement !== null ? `${Math.round(responseAgreement * 100)}%` : '-';
        },
      },
      {
        id: 'systemInstructionsAgreement',
        header: 'SI Agreement',
        cell: function ResponseAgreementCell(props: any) {
          const { systemInstructionsAgreement } = props.row.original;
          return systemInstructionsAgreement !== null
            ? `${Math.round(systemInstructionsAgreement * 100)}%`
            : '-';
        },
      },
      {
        id: 'completedAt',
        header: 'Audited At',
        cell: function CompletedAtCell(props: any) {
          const { completedAt } = props.row.original;
          return new Date(completedAt).toLocaleString();
        },
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Typography.Subtitle2>Dolphin QA Tasks</Typography.Subtitle2>{' '}
      </div>
      {isLoading ? (
        <LoadingTable columns={DolphinAuditTasksTableColumns} numRows={1} rowHeight={1} />
      ) : (
        <Table columns={DolphinAuditTasksTableColumns} data={tasks || []} />
      )}
    </div>
  );
};
